import { defineStore, storeToRefs } from "pinia";
import {
  BookingsEventsApi,
  DocumentsApi,
  SessionNotificationsApi,
  SessionsApi,
  TemplatesApi,
  type ApiResponse,
  type TriggerNotificationDto,
} from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "../sessions/session-details-store";
import { useSessionDetailsDocumentsStore } from "../sessions/session-details-documents-store";
import { useSessionDetailsTimeslotsStore } from "../sessions/session-details-timeslots-store";
import { useCompaniesStore } from "../companies/companies.store";
import { useAuthStore } from "../auth/auth.store";
import { RolesEnums } from "~/utils/enums/roles.enums";
import type { INotifications } from "~/utils/interfaces/notifications-interfaces";
import { typeNotications } from "~/utils/enums/notifications.enums";
import { useDocumentsStore } from "~/store/documents/documents.store";
import { BookingEventsEnum } from "~/utils/enums/bookings-events.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useRawSDKApi, useSDKApi } from "~/composables/api/useSDKApi";
import { FetchRedirectEnum, ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { UserLanguageEnum } from "~/utils/enums/users.enums";
import { convocationSuccessMessage, notificationSuccessMessage } from "~/utils/helpers/convocationFeedback";
import { splitTrainingPartnerContacts } from "~/utils/helpers/trainingPartnerContacts";
import {
  buildPartnerNotificationPayload,
  hasMailContent,
  partnerNotificationRequestError,
} from "~/utils/helpers/session-notifications/partner-notification";
import {
  buildTrainerNotificationPayload,
  sessionTrainers,
  trainerNotificationRequestError,
  trainerSendFeedback,
} from "~/utils/helpers/session-notifications/trainer-notification";
import {
  documentLinkDownloadUrl,
  documentLinkTarget,
} from "~/utils/helpers/session-notifications/document-link-target";
import { documentLinkFromResponse } from "~/utils/helpers/session-notifications/document-link-state";
import { DocumentLinkKindEnum, DocumentLinkStateEnum } from "~/utils/enums/documents.enums";
import { SessionNotificationDeliveryEnum } from "~/utils/enums/session-notifications.enums";
import type { IDocumentLink, IDocumentLinkTarget } from "~/utils/interfaces/session-notifications-interfaces";

/** Asks the orders route behind a partner-mail link without following its redirect to S3. */
async function probeDocumentLink(target: IDocumentLinkTarget): Promise<ApiResponse<void>> {
  if (target.kind === DocumentLinkKindEnum.ATTENDEE_LIST) {
    const apiInstance = await useRawSDKApi(MicroserviceEnum.ORDERS, SessionNotificationsApi, false);
    return apiInstance.downloadAttendeeListRaw({ id: target.credential }, { redirect: FetchRedirectEnum.MANUAL });
  }

  const apiInstance = await useRawSDKApi(MicroserviceEnum.ORDERS, DocumentsApi, false);
  // The generated method takes no `token`: orders reads it without declaring an `@ApiQuery`.
  return apiInstance
    .withPreMiddleware(async ({ url, init }) => ({
      url: `${url}?token=${encodeURIComponent(target.credential)}`,
      init,
    }))
    .documentsControllerGetSignedUrlFromProtectedLinkRaw({ redirect: FetchRedirectEnum.MANUAL });
}

export const useNotificationsStore = defineStore("notificationsStore", {
  state: (): INotifications => ({
    createSession: {
      initDocument: {
        itemId: null,
        itemType: null,
        displayValue: null,
        attendees: [],
      },
      attendees: [],
      countAttendees: null,
      emails: null,
      emailsCopy: [],
      doSendManager: false,
      subject: null,
      content: null,
      documents: [],
      selectedDocuments: null,
      batchDocuments: [],
      choiceDelivery: RolesEnums.EMPLOYEE,
      metadata: {
        sendIcs: false,
        sendUrlSession: true,
      },
      events: [],
    },
    providerNotification: {
      recipients: [],
      includeAttendeeList: false,
      sendError: null,
    },
    trainerNotification: {
      recipients: [],
      withoutEmail: [],
      sendIcs: true,
      sendUrlSession: true,
      sendError: null,
    },
    type: typeNotications.session,
    actionType: null,
    notifyModal: false,
    deliveryChoiceModal: false,
    deliveryChoice: null,
    showDocumentModal: false,
    listTemplates: [],
    templateById: null,
    createTemplateForm: {
      name: "[Nouveau modèle]",
      subject: "",
      content: "",
      itemType: "session",
    },
  }),
  getters: {
    isForEmployeeSession(): boolean {
      return this.createSession.choiceDelivery === RolesEnums.EMPLOYEE;
    },
    isForManagerSession(): boolean {
      return this.createSession.choiceDelivery === RolesEnums.MANAGER;
    },
  },
  actions: {
    /**
     * Build one notification event per selected attendee.
     *
     * Returns how many attendees had to be SKIPPED for lack of a resolvable `originalId` (the
     * booking id the backend keys the send on). Callers must refuse to send when that count is
     * non-zero: dropping recipients silently and reporting success meant an admin could convoke 3
     * people out of 5 and be told it worked.
     */
    notificationEvents(): number {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetailsAttendeesWithMail } = storeToRefs(sessionDetailsStore);
      let skipped = 0;

      if (this.type === typeNotications.session) {
        if (this.createSession.attendees?.length) {
          const existingDocsByItemId = new Map<string, string[]>();
          this.createSession.events?.forEach((event) => {
            if (event.documents?.length > 0) {
              existingDocsByItemId.set(event.itemId, [...event.documents]);
            }
          });

          this.createSession.events = this.createSession?.attendees
            ?.map((attendee) => {
              // When the attendee is not in the sessionDetailsAttendeesWithMail, we need to find the original_id from the sessionDetailsAttendeesWithMail
              const originalId =
                attendee?.originalId ||
                sessionDetailsAttendeesWithMail.value?.find((a) => a?.userId === attendee?.userId)?.originalId;

              // Return null if originalId is missing so we can filter it out — and count it, so the
              // caller can refuse to send a partial convocation rather than report a false success.
              if (!originalId) {
                skipped += 1;
                console.warn(
                  `Missing originalId for attendee with userId: ${attendee?.userId}. Skipping notification event.`,
                );
                return null;
              }

              const existingDocs = existingDocsByItemId.get(originalId) || [];

              return {
                itemId: originalId!,
                itemType: "booking",
                eventName: BookingEventsEnum.BOOKING_NOTIFICATION,
                status: null,
                toBeSentOn: null,
                documents: existingDocs,
              };
            })
            .filter((event): event is NonNullable<typeof event> => event !== null);
        }
      }

      return skipped;
    },
    async documentsLinks() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const feedback = useFeedback();
      const today = new Date();
      const endDate: Date = new Date(sessionDetails.value?.endDate ?? "");
      const twentyDaysInSeconds = 20 * 24 * 60 * 60;

      const seconds: number =
        endDate > today ? (endDate.getTime() - today.getTime()) / 1000 + twentyDaysInSeconds : twentyDaysInSeconds;

      const documentIds =
        this.createSession.selectedDocuments?.filter(
          (d) => !this.createSession.batchDocuments?.some((bd) => d.documentId === bd.documentId),
        ) || [];

      if (!documentIds.length) return;

      if (!this.createSession.events || this.createSession.events.length === 0) {
        this.notificationEvents();
      }

      try {
        const links = await useDocumentsStore().downloadBatchDocuments(
          documentIds.map((doc) => doc.documentId),
          Math.ceil(seconds),
        );
        const batchDocuments = links ?? [];

        if (batchDocuments.length && this.type === typeNotications.session) {
          this.createSession.batchDocuments?.push(...batchDocuments);

          batchDocuments.forEach((doc) => {
            this.createSession.events.find((e) => e.itemId === doc.itemId)?.documents.push(doc.link);
            if (doc.itemType === ItemType.SESSION) this.createSession.events.forEach((e) => e.documents.push(doc.link));
          });
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    resetSwitchDelivery(): void {
      if (this.isForEmployeeSession) this.createSession.doSendManager = false;

      if (this.type === typeNotications.session) {
        this.createSession.choiceDelivery === RolesEnums.EMPLOYEE
          ? this.createSession.attendees?.filter((email) => email).map((attendee) => attendee.email)
          : this.createSession.attendees
              ?.filter((e: any) => e.managerEmail)
              .map((attendee: any) => attendee.managerEmail);
      } else {
        this.createSession.emails = null;
      }

      this.createSession.emailsCopy = null;
      // this.createSession.metadata.sendIcs = false;
    },
    async template(language: string, sessionId?: string) {
      if (this.type === typeNotications.session && sessionId) {
        const feedback = useFeedback();
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsEventsApi);
          const template = await apiInstance.getNotificationTemplate({
            sessionId,
            toManagers: String(this.isForManagerSession),
            language,
          });
          const { cc, copyManagers } = template;

          this.createSession.doSendManager = copyManagers;
          this.createSession.emailsCopy = [];

          cc?.forEach((email: string) => {
            this.createSession.emailsCopy?.push({ email });
          });
        } catch {
          feedback.error("Une erreur est survenue", "small");
        }
      }
    },
    async sendNotificationSession(): Promise<void> {
      const feedback = useFeedback();

      if (!this.createSession.subject) {
        feedback.error("Le sujet de la notification est requis", "small");
        return;
      }

      // Populate events from attendees using the shared helper
      const skipped = this.notificationEvents();

      // A partial convocation is refused outright (product decision): silently dropping recipients
      // and reporting success let an admin convoke 3 people out of 5 and be told it worked. Runs
      // before documentsLinks() so a refused send costs no request.
      if (skipped > 0) {
        feedback.error(
          `${skipped} participant${skipped > 1 ? "s n'ont" : " n'a"} pas pu être identifié${skipped > 1 ? "s" : ""} : aucune notification n'a été envoyée`,
          "small",
        );
        return;
      }

      // Nothing selected at all (as opposed to selected-but-unresolvable, handled above).
      if (!this.createSession.events?.length) {
        feedback.error("Aucun participant trouvé", "small");
        return;
      }

      // Add documents to events
      await this.documentsLinks();

      // Prepare notification payload
      const triggerNotificationDto: TriggerNotificationDto = {
        subject: this.createSession.subject!,
        cc: this.createSession.emailsCopy?.map((e: any) => e.email) || [],
        copyManagers: this.createSession.doSendManager || false,
        toManagers: this.isForManagerSession,
        content: this.createSession.content || "",
        eventName: BookingEventsEnum.BOOKING_NOTIFICATION,
        // `metadata` is a free-form bag the DTO passes through verbatim, and orders reads snake keys.
        metadata: {
          send_ics: this.createSession.metadata.sendIcs,
          send_url_session: this.createSession.metadata.sendUrlSession,
        },
        events: this.createSession.events.map((event) => ({
          itemType: event.itemType,
          itemId: event.itemId,
          documents: event.documents,
        })),
      };

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsEventsApi);
        // Send notification event
        await apiInstance.triggerNotificationEvent({ triggerNotificationDto });
        feedback.success(notificationSuccessMessage(this.createSession.events.length), "small");
      } catch {
        feedback.error("Une erreur est survenue lors de l'envoi de la notification", "small");
      }
    },
    /**
     * Opens the full-page provider send form, pre-filled from the session.
     *
     * The provider contact lives on the session header as a single `trainingPartnerEmail`, while the
     * form shows one chip per address — so the string is split on the separators an admin can type.
     */
    async openProviderNotification(sessionId: string): Promise<void> {
      const sessionDetailsStore = useSessionDetailsStore();
      const companiesStore = useCompaniesStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);

      this.$patch({
        providerNotification: {
          recipients: splitTrainingPartnerContacts(sessionDetails.value?.trainingPartnerEmail),
          includeAttendeeList: false,
          sendError: null,
        },
      });
      await this.prepareSessionSendForm(sessionId);

      // Subject falls back on « formation & client » when no template carries one. The session page
      // never loads `companyInfos`: the profile's entity names the client there.
      const companyName = companiesStore.companyInfos?.name ?? useAuthStore().user?.mainEntityId?.name;
      this.createSession.subject = sessionDetails.value?.trainingTitle
        ? [`Formation ${sessionDetails.value.trainingTitle}`, companyName].filter(Boolean).join(" & ")
        : null;
      // Last: the form mounts on it and reads the state set above.
      this.deliveryChoice = SessionNotificationDeliveryEnum.TRAINING_PARTNER;
    },
    /** Starts a provider or trainer send form clean, with the session's documents to attach. */
    async prepareSessionSendForm(sessionId: string): Promise<void> {
      // $patch deep-merges: createSession and initDocument keep every field not listed here.
      this.$patch({
        // The template applied here is the one the send references: never one left by another screen.
        templateById: null,
        createSession: {
          subject: null,
          content: null,
          emailsCopy: [],
          selectedDocuments: [],
          batchDocuments: [],
          initDocument: { itemId: sessionId, itemType: ItemType.SESSION, displayValue: "tous" },
        },
      });
      await this.loadSessionDocuments(sessionId);
    },
    /**
     * The documents « Joindre des documents » offers on a partner or trainer send, read again after an
     * upload made from the form — nothing else populates them on these entry points.
     * Session level only: orders refuses an attendee's own document on both sends.
     */
    async loadSessionDocuments(sessionId: string): Promise<void> {
      const sessionDetailsDocumentsStore = useSessionDetailsDocumentsStore();

      await sessionDetailsDocumentsStore.fetchSessionDocumentsWithPagination(sessionId, { limit: 10000 });
      this.createSession.documents = (sessionDetailsDocumentsStore.sessionDocumentsPagination?.data ?? []).filter(
        (document) => document.itemType === ItemType.SESSION,
      );
    },
    /**
     * Mails the session's partner contact (AC-ENVOI.15). Orders sends first and records second, so a
     * refusal leaves nothing behind; its reason is kept in `sendError`, shown next to the button.
     */
    async sendProviderNotification(sessionId: string): Promise<boolean> {
      this.providerNotification.sendError = null;

      // Covers both « the session carries no contact » and « the user deselected every chip ».
      if (!this.providerNotification.recipients.length) {
        this.providerNotification.sendError = "Sélectionnez au moins un destinataire.";
        return false;
      }
      if (!this.createSession.subject?.trim()) {
        this.providerNotification.sendError = "Le sujet de la notification est requis.";
        return false;
      }
      if (!hasMailContent(this.createSession.content)) {
        this.providerNotification.sendError = "Le contenu du mail est requis.";
        return false;
      }

      const sendPartnerNotificationDto = buildPartnerNotificationPayload({
        recipients: this.providerNotification.recipients,
        cc: this.createSession.emailsCopy?.map((recipient: { email?: string }) => recipient.email ?? "") ?? [],
        subject: this.createSession.subject,
        content: this.createSession.content,
        templateId: this.templateById?.templateId ?? null,
        // What the form lists as attached: orders builds the protected links itself.
        documentIds: this.createSession.batchDocuments?.map((document) => document.documentId) ?? [],
        includeAttendeeList: this.providerNotification.includeAttendeeList,
      });

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionNotificationsApi);
        await apiInstance.sendPartnerNotification({ sessionId, sendPartnerNotificationDto });
        useFeedback().success("La notification a bien été envoyée au contact prestataire", "small");
        return true;
      } catch (error) {
        this.providerNotification.sendError = await partnerNotificationRequestError(error);
        return false;
      }
    },
    /**
     * Opens the full-page trainer send form on the trainers named on the session's steps, each once.
     * Orders refuses the whole send for a single trainer without an address: those are set aside.
     */
    async openTrainerNotification(sessionId: string): Promise<void> {
      const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
      // The chips must be the trainers on the steps now: a failed re-read (already reported) opens no form.
      if (!(await sessionDetailsTimeslotsStore.fetchSessionTimeslots())) return;
      const trainers = sessionTrainers(sessionDetailsTimeslotsStore.sessionTimeslots);

      this.$patch({
        trainerNotification: {
          recipients: trainers.filter((trainer) => trainer.email),
          withoutEmail: trainers.filter((trainer) => !trainer.email),
          sendIcs: true,
          sendUrlSession: true,
          sendError: null,
        },
      });
      await this.prepareSessionSendForm(sessionId);
      // Last: the form mounts on it and reads the state set above.
      this.deliveryChoice = SessionNotificationDeliveryEnum.TRAINERS;
    },
    /**
     * Mails each selected trainer, with an invitation for the steps they lead when `sendIcs` is on.
     * A refusal records nothing and its reason stays next to the button; once recorded, the mails
     * leave through orders' outbox and one that fails later shows as « Échec » in the table.
     */
    async sendTrainerNotification(sessionId: string): Promise<boolean> {
      this.trainerNotification.sendError = null;

      if (!this.trainerNotification.recipients.length) {
        this.trainerNotification.sendError = "Sélectionnez au moins un formateur.";
        return false;
      }
      if (!this.createSession.subject?.trim()) {
        this.trainerNotification.sendError = "Le sujet de la notification est requis.";
        return false;
      }
      if (!hasMailContent(this.createSession.content)) {
        this.trainerNotification.sendError = "Le contenu du mail est requis.";
        return false;
      }

      const sendTrainerNotificationDto = buildTrainerNotificationPayload({
        trainerUserIds: this.trainerNotification.recipients.map((trainer) => trainer.userId),
        subject: this.createSession.subject,
        content: this.createSession.content,
        templateId: this.templateById?.templateId ?? null,
        documentIds: this.createSession.batchDocuments?.map((document) => document.documentId) ?? [],
        sendIcs: this.trainerNotification.sendIcs,
        sendUrlSession: this.trainerNotification.sendUrlSession,
      });

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionNotificationsApi);
        const detail = await apiInstance.sendTrainerNotification({ sessionId, sendTrainerNotificationDto });
        const outcome = trainerSendFeedback(detail);
        if (outcome.success) useFeedback().success(outcome.message, "small");
        else useFeedback().error(outcome.message, "small");
        return true;
      } catch (error) {
        this.trainerNotification.sendError = await trainerNotificationRequestError(error);
        return false;
      }
    },
    /**
     * State of a partner-mail link (`/link?document_link=`, AC-ENVOI.31), read without an account. The
     * raw client is on purpose: the probe reads the bare `Response` of a `redirect: manual` call, and
     * the `useSDKApi` proxy would turn `withPreMiddleware` into a promise.
     */
    async resolveDocumentLink(documentLink: unknown): Promise<IDocumentLink> {
      const target = documentLinkTarget(documentLink);
      if (!target) return { state: DocumentLinkStateEnum.NOT_FOUND, kind: null, expiredAt: null, downloadUrl: null };

      const downloadUrl = documentLinkDownloadUrl(useRuntimeConfig().public.ORDERS_URL, target);
      try {
        const response = await probeDocumentLink(target);
        return await documentLinkFromResponse(response.raw, target, downloadUrl);
      } catch (error) {
        return await documentLinkFromResponse((error as { response?: Response } | null)?.response, target, downloadUrl);
      }
    },
    async convoked(): Promise<void> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();

      if (this.createSession.attendees?.length) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);

        if (!this.createSession.subject) {
          feedback.error("Le sujet de la notification est requis", "small");
          return;
        }

        // Populate events from attendees using the shared helper
        const skipped = this.notificationEvents();

        // A partial convocation is refused outright (product decision): silently dropping recipients
        // and reporting success let an admin convoke 3 people out of 5 and be told it worked. Runs
        // before documentsLinks() so a refused send costs no request.
        if (skipped > 0) {
          feedback.error(
            `${skipped} participant${skipped > 1 ? "s n'ont" : " n'a"} pas pu être identifié${skipped > 1 ? "s" : ""} : aucune convocation n'a été envoyée`,
            "small",
          );
          return;
        }

        // Nothing selected at all (as opposed to selected-but-unresolvable, handled above).
        if (!this.createSession.events?.length) {
          feedback.error("Aucun participant trouvé", "small");
          return;
        }

        // Add documents to events
        await this.documentsLinks();

        // Prepare notification payload
        const payload: TriggerNotificationDto = {
          subject: this.createSession.subject!,
          cc: this.createSession.emailsCopy?.map((e: any) => e.email) || [],
          copyManagers: this.createSession.doSendManager || false,
          toManagers: this.isForManagerSession,
          content: this.createSession.content || "",
          eventName: BookingEventsEnum.BOOKING_NOTIFICATION,
          // `metadata` is a free-form bag the DTO passes through verbatim, and orders reads snake keys.
          metadata: {
            send_ics: this.createSession.metadata.sendIcs,
            send_url_session: this.createSession.metadata.sendUrlSession,
          },
          events: this.createSession.events.map((event) => ({
            itemType: event.itemType,
            itemId: event.itemId,
            documents: event.documents,
          })),
        };

        try {
          await apiInstance.summonSessionParticipants({
            triggerNotificationDto: payload,
          });
          feedback.success(convocationSuccessMessage(this.createSession.events.length), "small");
          if (sessionDetailsStore.sessionDetails?.sessionId) {
            await sessionDetailsStore.fetchSessionDetails(sessionDetailsStore.sessionDetails?.sessionId, this.type);
          }
        } catch (error) {
          console.error("Error summoning session participants:", error);
          feedback.error("Une erreur est survenue lors de l'envoi de la convocation", "small");
        }
      } else {
        feedback.error("Aucun participant trouvé", "small");
      }
    },
    async getTemplates(scope?: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        const list = await apiInstance.getListTemplates({
          itemType: scope,
        });
        this.listTemplates = list;
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async getFilledTemplate(templateId: string, sessionId: string, itemType: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        const template = await apiInstance.getFilledTemplate({
          templateId,
          itemId: sessionId,
          itemType,
        });
        return template;
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async getOneTemplate(id: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        const template = await apiInstance.getOneTemplate({ templateId: id });
        this.templateById = template;
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async deleteTemplate(id: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        await apiInstance.deleteTemplate({ templateId: id });

        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async updateTemplate(templateId: string): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        await apiInstance.updateTemplate({
          templateId,
          updateTemplateDto: {
            name: this.createTemplateForm.name,
            subject: this.createTemplateForm.subject,
            content: this.createTemplateForm.content,
          },
        });

        feedback.success("Vos modifications ont bien été sauvegardées", "small");
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async updateTemplateIsDefault(id: string, isDefault: boolean): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        await apiInstance.updateTemplate({
          templateId: id,
          updateTemplateDto: {
            isDefault: isDefault || false,
          },
        });
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
    async createTemplate(): Promise<string | undefined> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);

      try {
        const data = await apiInstance.createTemplate({
          createTemplateDto: {
            name: this.createTemplateForm.name,
            subject: this.createTemplateForm.subject,
            content: this.createTemplateForm.content,
            itemType: this.createTemplateForm.itemType,
          },
        });
        feedback.success("Vos modifications ont bien été sauvegardées", "small");

        return data.templateId;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return undefined;
      }
    },
    templateContentDefault(): string {
      const companiesStore = useCompaniesStore();
      const authStore = useAuthStore();

      const fr = `
      <p>Bonjour,</p><p></p>
<p>Nous vous informons de votre inscription à la formation <strong>{{training_name}}</strong> par <strong>{{provider_name}}</strong>.</p>
<p></p>

<p>Quelques informations pratiques :</p>
<ul>
  <li>Dates : du {{session_startDate}} {{session_startHour}} au {{session_endDate}} {{session_endHour}}</li>
  <li>Lieu : {{session_location}}</li>
</ul>
<p></p>
<p>Contact en cas de question : {{provider_contact}}</p>
<p></p>
<p>A très bientôt,<br>
L’équipe RH ${companiesStore.companyInfos?.name}</p>
      `;

      const en = `<p>Hello,</p><p></p>
<p>We would like to inform you that you have registered for <strong>{{training_name}}</strong> by <strong>{{provider_name}}</strong>.</p>
<p></p>
<p>Some practical information:</p>
<ul>
  <li>Dates: from {{session_startDate}} {{session_startHour}} to {{session_endDate}} {{session_endHour}}</li>
  <li>Location : {{session_location}}</li>
</ul>
<p></p>
<p>Contact for questions: {{provider.contact}}</p><p></p>

<p>See you soon,<br>
The ${companiesStore.companyInfos?.name} HR Team</p>`;

      return authStore.user?.language === UserLanguageEnum.FR ? fr : en;
    },
    templateSubjectDefault(): string {
      const authStore = useAuthStore();

      const fr = `Inscription à la formation {{training_name}}`;
      const en = `Registration for {{training_name}}`;

      return authStore.user?.language === UserLanguageEnum.FR ? fr : en;
    },
  },
});
